import React, { useRef, useMemo, useEffect } from 'react';

import styled from 'styled-components';

import LockerInfo from './LockerInfo';
import CustomScrollbar from '../../Model/Share/Normal/CustomScrollbar';

export default function LockerGrid(props) {
  const {
    lockerData,
    divisionIndex,
    LockerRef,
    clickUsageHistoryFn,
    clickFunctionFn,
    clickChangeStateFn,
    clickDeleteLockerFn,
    clickChangeNameFn,
    clickSendMessageFn,
    clickExtendFn
  } = props;

  const bodyRef = useRef(null);

  // 락커 좌표 중복 여부 검사 //
  function hasDuplicateCoords() {
    const seenCoords = new Set();

    for (const locker of lockerData.lockers) {
      const key = `${locker.x ?? 0},${locker.y ?? 0}`;

      if (seenCoords.has(key)) return true;

      seenCoords.add(key);
    }

    return false;
  }

  // 좌표 기반 그리드 cell 배열 생성 //
  function buildCoordinateCells() {
    let maxX = 0;
    let maxY = 0;
    const coordMap = new Map();

    for (let i = 0; i < lockerData.lockers.length; i++) {
      const locker = lockerData.lockers[i];
      const x = locker.x ?? 0;
      const y = locker.y ?? 0;

      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;

      coordMap.set(`${x},${y}`, { locker, idx: i });
    }

    const cols = maxX + 1;
    const rows = maxY + 1;
    const cells = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        cells.push(coordMap.get(`${c},${r}`) ?? null);
      }
    }

    return { cols, cells };
  }

  // 좌표가 없으면 7열 폴백, 있으면 좌표 기반 그리드 //
  function getGridLayout() {
    if (hasDuplicateCoords()) {
      return {
        cols: Math.min(lockerData.lockers.length, 7),
        cells: lockerData.lockers.map((locker, i) => ({ locker, idx: i }))
      };
    }

    return buildCoordinateCells();
  }

  // 락커 셀 ref 설정 //
  function setLockerRef(element, lockerIndex) {
    if (!LockerRef.current[divisionIndex]) LockerRef.current[divisionIndex] = [];

    LockerRef.current[divisionIndex][lockerIndex] = element;
  }

  // 락커 셀 렌더링 //
  function renderCell(cell, index) {
    if (cell === null) return <LockerEmptySlot key={'EmptySlot-' + index} />;

    return (
      <LockerInfo
        key={'Locker-' + cell.locker.id}
        data={cell.locker}
        index={divisionIndex}
        lockerIndex={cell.idx}
        ref={element => setLockerRef(element, cell.idx)}
        clickUsageHistoryFn={clickUsageHistoryFn}
        clickFunctionFn={clickFunctionFn}
        clickChangeStateFn={clickChangeStateFn}
        clickDeleteLockerFn={clickDeleteLockerFn}
        clickChangeNameFn={clickChangeNameFn}
        clickSendMessageFn={clickSendMessageFn}
        clickExtendFn={clickExtendFn} />
    );
  }

  const { cols, cells } = useMemo(() => getGridLayout(), [lockerData.lockers]);

  // 가로 스크롤이 가능할 때 마우스 휠을 가로 스크롤로 전환 //
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    function onWheel(e) {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [cols]);

  return (
    <GridOuterWrapper>
      <GridScrollWrapper ref={bodyRef}>
        <InfoBox $cols={cols}>
          {cells.map(renderCell)}
        </InfoBox>
      </GridScrollWrapper>
      <CustomScrollbarWrapper>
        <CustomScrollbar
          direction='horizontal'
          scrollRef={bodyRef}
          isColorPrimary={true}
          isTrackThin={true}
          showTrackAlways={false} />
      </CustomScrollbarWrapper>
    </GridOuterWrapper>
  );
}

const GridOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const GridScrollWrapper = styled.div`
  width: 100%;

  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CustomScrollbarWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  margin-top: 6px;
`;

const InfoBox = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols}, 122px);

  width: fit-content;

  gap: 10px 24px;
`;

const LockerEmptySlot = styled.div`
  width: 122px;
  height: 122px;
`;
